import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiErrorDetail } from "@/lib/api";

const NAMES = ["Contractor", "Retail", "Designer"];

export default function DocumentTemplatesSettings() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/settings/document-templates");
    setTemplates(data.templates || {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (name, field, value) =>
    setTemplates((cur) => ({
      ...cur,
      [name]: { ...(cur[name] || {}), [field]: value },
    }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/document-templates", {
        templates,
      });
      setTemplates(data.templates || {});
      toast.success("Document templates saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the templates",
      );
    }
    setBusy(false);
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Document templates" />
      <main className="relative z-10 mx-auto max-w-3xl px-4 pb-36 pt-6">
        <button
          type="button"
          onClick={() => nav("/settings")}
          data-testid="doc-templates-back"
          className="mb-5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-zinc-500 transition-colors hover:text-zinc-300"
        >
          <ArrowLeft size={14} weight="bold" /> Settings
        </button>
        <p className="mb-6 max-w-2xl text-sm text-zinc-400">
          These named templates drive the printed Quote, Invoice and Sales /
          Change Order PDFs. Pick a template on each account under Profile →
          Document Templates. &ldquo;Default&rdquo; uses your standard Quote
          Template wording.
        </p>

        <div className="space-y-5" data-testid="document-templates">
          {NAMES.map((name) => {
            const t = templates[name] || {};
            const slug = name.toLowerCase();
            return (
              <div
                key={name}
                data-testid={`doc-template-${slug}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
              >
                <h3 className="text-sm font-bold uppercase tracking-widest text-indigo-300">
                  {name}
                </h3>
                <label className="mt-4 block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Notice line
                  </span>
                  <Input
                    data-testid={`doc-${slug}-notice`}
                    value={t.notice_text || ""}
                    onChange={(e) => set(name, "notice_text", e.target.value)}
                    className="mt-1 h-9 border-zinc-800 bg-zinc-950 text-sm"
                    placeholder="Short line shown under the header"
                  />
                </label>
                <label className="mt-4 block">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Terms
                  </span>
                  <textarea
                    data-testid={`doc-${slug}-terms`}
                    rows={3}
                    value={t.terms_text || ""}
                    onChange={(e) => set(name, "terms_text", e.target.value)}
                    className="mt-1 w-full resize-y rounded-md border border-zinc-800 bg-zinc-950 p-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="Payment terms printed at the bottom of the document"
                  />
                </label>
                <label className="mt-4 flex items-center gap-2 text-sm text-zinc-200">
                  <input
                    type="checkbox"
                    data-testid={`doc-${slug}-pieces`}
                    checked={!!t.show_piece_lines}
                    onChange={(e) =>
                      set(name, "show_piece_lines", e.target.checked)
                    }
                    className="h-4 w-4 accent-indigo-500"
                  />
                  Show per-piece detail lines on quotes
                </label>
              </div>
            );
          })}
        </div>

        <div className="mt-6">
          <Button
            data-testid="doc-templates-save"
            onClick={save}
            disabled={busy}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            <FloppyDisk size={15} weight="bold" className="mr-2" /> Save
            templates
          </Button>
        </div>
      </main>
    </div>
  );
}
